import React, { useState, useEffect, useRef, useCallback } from "react";

import { 
  Campaign, 
  Encounter,
} from "@dndModels";
import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import LoadingBox from "@components/input/LoadingBox";
import ListOptionsInput from "@components/Navigation/ListOptionsInput";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import EncounterParent from "@dndComponents/encounter/EncounterParent";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import CommonUtilities from "@utilities/common_utilities";
import FlexBox from "@components/layout/FlexBox";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  campaign: Campaign;
}

export default function CampaignEncountersInput({ campaign }: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selected, setSelected] = useState<Encounter | undefined>(undefined);
  const [change_time, setChangeTime] = useState(0);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    // debugger;
    if (campaign.encounters.length === 0) {
      campaign.encounters = await api.getCampaignObjects<Encounter>({ data_type: "encounter", campaign });
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderExtras = () => {
    return (
      <ModalOrDrawer 
        open={!!selected} 
        onClose={() => { setSelected(undefined); }}
        full_page>
        <EncounterParent
          id={ selected ? selected._id : "" }
          encounter={selected}
        />
      </ModalOrDrawer>
    )
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <FlexBox direction="column">
        <FlexBox className="padded">
          <AddButton
            disabled={processing}
            onClick={ async () => {
              if (api.user) {
                setProcessing(true);
                const new_encounter = new Encounter();
                new_encounter.campaign_id = campaign._id;
                new_encounter.campaign = campaign;
                await api.upsertObject("encounter", new_encounter);
                campaign.encounters.push(new_encounter);
                setSelected(new_encounter);
                setProcessing(false);
                setChangeTime(common_util.get_time_number());
              }
            }}
          />
        </FlexBox>
        <FlexBox>
          <ListOptionsInput
            data_type="encounter"
            use_objects={campaign.encounters}
            no_filter
            change_time={change_time}
            filter_on_change
            onSelect={(obj) => {
              if (obj instanceof Encounter) {
                setSelected(obj);
              } else {
                setSelected(undefined);
              }
            }}
            onDelete={ async (obj) => {
              if (obj instanceof Encounter) {
                setProcessing(true);
                await api.deleteObject("encounter", obj);
                campaign.encounters = campaign.encounters.filter(o => o._id !== obj._id);
                setProcessing(false);
                setChangeTime(common_util.get_time_number());
              }
            }}
          />
        </FlexBox>
        { renderExtras() }
      </FlexBox>
    );
  } 
}
